import React, { useEffect, useRef, useState } from "react";
import { ExpertAvatar } from "../../ExpertAvatarSDK/ExpertAvatar.js";
import { DIGITAL_HUMAN_URL, EDUGATE_API_URL } from "../../../config/serviceConfig.js";
import "./ScienceExpoDigitalHuman.css";

const getFallbackConfig = (roleName) => ({
  id: String(roleName || "science_expo_default").replace(/\s+/g, "_"),
  name: roleName || "科学圆桌角色",
  avatarPath: "/assets/avatars/strategy_executor.zip",
  voice: "龙橙",
  model_name: "cosyvoice-v1",
  _fallback: true,
});

const DIAG_PREFIX = "[录课数字人诊断]";

const buildSessionUser = (currentUser) => ({
  user_id: String(
    currentUser?.unified_user_id ||
      currentUser?.userId ||
      currentUser?.studentID ||
      currentUser?.student_id ||
      currentUser?.employeeNumber ||
      currentUser?.username ||
      ""
  ),
  username: currentUser?.username || "",
  display_name: currentUser?.name || currentUser?.displayName || currentUser?.username || "",
  user_type:
    currentUser?.user_type ||
    (currentUser?.isCasUser ? "bupt_cas" : currentUser?.isSchoolUser ? "campus" : currentUser ? "guest" : ""),
});

export const fetchDigitalHumanConfig = async (roleName, workflowName, messageType) => {
  try {
    console.log(`${DIAG_PREFIX} 配置请求`, {
      roleName,
      workflowName,
      messageType,
    });
    const response = await fetch(`${EDUGATE_API_URL}/api/digital-human/config`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        role_name: roleName,
        workflow_name: workflowName,
        message_type: messageType,
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const result = await response.json();
    console.log(`${DIAG_PREFIX} 配置响应`, {
      roleName,
      workflowName,
      messageType,
      code: result?.code,
      message: result?.message,
      data: result?.data,
    });
    if (result.code === 200 && result.data) {
      const resolvedName = result.data.role_name || roleName || "科学圆桌角色";
      const scopedId = `${workflowName || "default"}:${resolvedName}`;
      console.log(`${DIAG_PREFIX} voice配置`, {
        requestRoleName: roleName,
        resolvedName,
        workflowName,
        messageType,
        scopedId,
        avatarPath: result.data.avatar_path,
        avatarVoice: result.data.avatar_voice,
        voiceId: result.data.voice_id,
        voiceName: result.data.voice_name,
        raw: result.data,
      });
      return {
        id: String(scopedId).replace(/\s+/g, "_"),
        name: resolvedName,
        avatarPath: result.data.avatar_path,
        voice: result.data.avatar_voice,
        model_name: "cosyvoice-v1",
      };
    }
  } catch (error) {
    console.warn("[ScienceExpoDigitalHuman] 获取配置失败，使用默认数字人:", error);
  }

  return getFallbackConfig(roleName);
};

const getAvatarPreviewUrl = (avatarPath) => {
  if (!avatarPath) return "";
  const previewPath = String(avatarPath).replace(/\.zip(\?.*)?$/i, ".png$1");
  return previewPath.startsWith("http") ? previewPath : `${DIGITAL_HUMAN_URL}${previewPath}`;
};

const getWsStateName = (ws) => {
  if (!ws) return "null";
  if (ws.readyState === WebSocket.CONNECTING) return "CONNECTING";
  if (ws.readyState === WebSocket.OPEN) return "OPEN";
  if (ws.readyState === WebSocket.CLOSING) return "CLOSING";
  if (ws.readyState === WebSocket.CLOSED) return "CLOSED";
  return String(ws.readyState);
};

// ExpertAvatar 的 WebGL 清屏色为 #020611。
const KEY_BACKGROUND = { r: 2, g: 6, b: 17 };
const KEY_CLEAR_DISTANCE = 10; // 颜色距离 ≤ 此值 → 判定为纯背景色
const KEY_FEATHER_DISTANCE = 30; // 颜色距离 < 此值 → 背景色候选（羽化区间上限）

/**
 * 黑底抠除。每帧一次全画布 getImageData（GPU 回读）+ flood fill，是主线程上最贵的一段。
 *
 * ⚠️ 必须只对当前发言的那一个实例开启。展示模式会把花名册里 12 个角色全部 mount，
 * 若每个实例都无条件跑这个循环，主线程会被 12 路回读打满：实测 loadAvatar 的耗时
 * 从 3s 一路劣化到 240s，数字人音频到了也迟迟开不了口。非 active 的实例本来就是
 * visibility:hidden，抠不抠都看不见。
 *
 * @returns {{ stop: () => void, setEnabled: (on: boolean) => void }}
 */
const startBlackBackgroundKeying = (container) => {
  if (!container) return { stop: () => {}, setEnabled: () => {} };

  let rafId = 0;
  let stopped = false;
  let enabled = false;
  let outputCanvas = null;
  let outputCtx = null;
  let sourceCanvas = null;

  // 复用的位图缓冲区，随画布尺寸变化重新分配，避免每帧产生 GC 压力
  let bufferWidth = 0;
  let bufferHeight = 0;
  let distanceBuffer = null; // Float32Array：像素颜色到清屏色的欧氏距离
  let isBgCandidateBuffer = null; // Uint8Array：是否落入背景色候选范围（未做连通性判断）
  let connectedBuffer = null; // Uint8Array：是否与画布边界连通的背景色候选
  let stackBuffer = null; // Int32Array：手写栈，做 flood fill

  const ensureBuffers = (width, height) => {
    if (bufferWidth === width && bufferHeight === height && distanceBuffer) return;
    bufferWidth = width;
    bufferHeight = height;
    const pixelCount = width * height;
    distanceBuffer = new Float32Array(pixelCount);
    isBgCandidateBuffer = new Uint8Array(pixelCount);
    connectedBuffer = new Uint8Array(pixelCount);
    stackBuffer = new Int32Array(pixelCount);
  };

  /**
   * 只把"背景色候选、且与画布四边连通"的像素判定为真正的背景。
   * 这样人物内部颜色同样接近清屏色的区域（黑发、深色衣物褶皱阴影）
   * 因为没有一条同色路径通到画布边缘，不会被误判、不会被抠空——
   * 效果等价于常见抠图软件里"从边缘取色再扩散"的连通域键控，
   * 比单纯按颜色距离全图抠除更不容易伤到人物本体。
   */
  const floodFillBackground = (width, height) => {
    let stackTop = 0;
    const pushIfCandidate = (idx) => {
      if (isBgCandidateBuffer[idx] && !connectedBuffer[idx]) {
        connectedBuffer[idx] = 1;
        stackBuffer[stackTop++] = idx;
      }
    };

    for (let x = 0; x < width; x++) {
      pushIfCandidate(x);
      pushIfCandidate((height - 1) * width + x);
    }
    for (let y = 0; y < height; y++) {
      pushIfCandidate(y * width);
      pushIfCandidate(y * width + (width - 1));
    }

    while (stackTop > 0) {
      const idx = stackBuffer[--stackTop];
      const x = idx % width;
      const y = (idx - x) / width;
      if (x > 0) pushIfCandidate(idx - 1);
      if (x < width - 1) pushIfCandidate(idx + 1);
      if (y > 0) pushIfCandidate(idx - width);
      if (y < height - 1) pushIfCandidate(idx + width);
    }
  };

  const cleanup = () => {
    stopped = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (sourceCanvas) {
      sourceCanvas.style.opacity = "";
    }
    outputCanvas?.remove();
  };

  const processFrame = () => {
    if (stopped || !enabled || !sourceCanvas || !outputCanvas || !outputCtx) return;

    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    if (!width || !height) {
      rafId = requestAnimationFrame(processFrame);
      return;
    }

    if (outputCanvas.width !== width || outputCanvas.height !== height) {
      outputCanvas.width = width;
      outputCanvas.height = height;
    }
    ensureBuffers(width, height);

    try {
      outputCtx.clearRect(0, 0, width, height);
      outputCtx.drawImage(sourceCanvas, 0, 0, width, height);
      const frame = outputCtx.getImageData(0, 0, width, height);
      const data = frame.data;
      const pixelCount = width * height;

      connectedBuffer.fill(0);
      for (let p = 0; p < pixelCount; p++) {
        const idx = p * 4;
        const dr = data[idx] - KEY_BACKGROUND.r;
        const dg = data[idx + 1] - KEY_BACKGROUND.g;
        const db = data[idx + 2] - KEY_BACKGROUND.b;
        const distance = Math.sqrt(dr * dr + dg * dg + db * db);
        distanceBuffer[p] = distance;
        isBgCandidateBuffer[p] = distance <= KEY_FEATHER_DISTANCE ? 1 : 0;
      }

      floodFillBackground(width, height);

      for (let p = 0; p < pixelCount; p++) {
        if (!connectedBuffer[p]) continue; // 未连通到边界 → 视为人物本体，保留原始 alpha
        const idx = p * 4;
        const distance = distanceBuffer[p];
        if (distance <= KEY_CLEAR_DISTANCE) {
          data[idx + 3] = 0;
        } else {
          data[idx + 3] = Math.round(
            ((distance - KEY_CLEAR_DISTANCE) / (KEY_FEATHER_DISTANCE - KEY_CLEAR_DISTANCE)) *
              data[idx + 3]
          );
        }
      }

      outputCtx.putImageData(frame, 0, 0);
      sourceCanvas.style.opacity = "0";
    } catch (error) {
      console.warn("[ScienceExpoDigitalHuman] 数字人黑底抠除失败，回退原始 canvas:", error);
      sourceCanvas.style.opacity = "";
      outputCanvas.remove();
      return;
    }

    rafId = requestAnimationFrame(processFrame);
  };

  const tryAttach = () => {
    if (stopped || !enabled) return;
    sourceCanvas = container.querySelector("canvas");
    if (!sourceCanvas) {
      rafId = requestAnimationFrame(tryAttach);
      return;
    }

    if (!outputCanvas) {
      outputCanvas = document.createElement("canvas");
      outputCanvas.className = "science-expo-keyed-canvas";
      outputCtx = outputCanvas.getContext("2d", { willReadFrequently: true });
      if (!outputCtx) return;
      container.appendChild(outputCanvas);
    }
    processFrame();
  };

  return {
    stop: cleanup,
    setEnabled(next) {
      const on = !!next;
      if (stopped || enabled === on) return;
      enabled = on;
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      // 关闭时保留最后一帧和 outputCanvas：实例此刻是隐藏的，
      // 重新激活时直接续跑，避免原始黑底 canvas 闪一帧出来。
      if (on) tryAttach();
    },
  };
};

const ScienceExpoDigitalHuman = ({
  isOpen = true,
  expertName,
  text = "",
  variant = "seat", // seat | spotlight
  workflowName = "",
  messageType = "science_expo",
  currentUser = null,
  active = false,
  mountReady = true, // P3 lazy mount 闸门：false 时 spotlight 实例只渲染 PNG 占位，不创建 ExpertAvatar
  configOverride = null,
  speechKey = "",
  silent = false,
  onPlaybackStart,
  onPlaybackEnd,
  onLoadingChange, // (loading: boolean, reason?: string) => void  仅 spotlight 模式有效
  controlRef,      // { current: { interrupt: () => void } }  仅 spotlight 模式注入
}) => {
  const containerRef = useRef(null);
  const avatarRef = useRef(null);
  const configRef = useRef(null);
  const queueRef = useRef([]);
  const silentRef = useRef(silent);
  silentRef.current = silent;
  const queueRunningRef = useRef(false);
  const tokenRef = useRef(0);
  const lastSpokenRef = useRef("");
  // 同步切换锁：switchExpert 开始时立刻置 true，完成后清除
  // 用 ref 而非 state，保证 drainQueue 能同步读到，不依赖 React render 周期
  const switchingRef = useRef(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("idle");
  const [previewFailed, setPreviewFailed] = useState(false);
  // 3D 形象是否已就绪（loadAvatar 完成）。false 时 spotlight 显示 PNG 占位。
  // 与 status 解耦：switchExpert 现在 session ready 即 resolve，音频可以先播，
  // 但 3D 还在异步 loadAvatar，此期间用 PNG 替代避免"旧形象做新表情"的串台。
  const [visualReady, setVisualReady] = useState(false);

  const isSpotlight = variant === "spotlight";
  const activeSpeechConfigKey = isSpotlight && active ? speechKey : "";
  const displayName = config?.name || expertName || "科学圆桌角色";
  const previewUrl = getAvatarPreviewUrl(config?.avatarPath);
  const showSpotlightPreview = isSpotlight
    && previewUrl
    && !previewFailed
    && !visualReady
    && (status === "loading" || status === "switching");

  // 把 interrupt 暴露给父组件（仅 spotlight）
  useEffect(() => {
    if (!isSpotlight || !controlRef) return undefined;
    controlRef.current = {
      interrupt: () => {
        // 1. 清空待播队列（不清 lastSpokenRef！否则 React re-render
        //    会让 text useEffect 误以为是新文本，重新 push 入队 → 重播）
        queueRef.current = [];
        // 2. 把当前 text 也写入 lastSpokenRef，防止 useEffect 触发时再次入队
        const curText = String(text || "").trim();
        if (curText) lastSpokenRef.current = `${speechKey || "default"}:${curText}`;
        // 3. 调 SDK 打断当前播放（清 processor → resolvePlaybackEnd
        //    → drainQueue 的 await playbackDone 立刻返回 → 看到队列空就退出）
        avatarRef.current?.interrupt?.();
      },
    };
    return () => {
      if (controlRef.current) controlRef.current = null;
    };
  }, [isSpotlight, controlRef, text, speechKey]);

  useEffect(() => {
    let cancelled = false;

    const loadConfigAndPreload = async () => {
      const nextConfig = configOverride || await fetchDigitalHumanConfig(expertName, workflowName, messageType);
      if (cancelled) return;

      configRef.current = nextConfig;
      setConfig(nextConfig);
      setPreviewFailed(false);

      if (nextConfig.avatarPath) {
        ExpertAvatar.preloadModel(nextConfig.avatarPath, DIGITAL_HUMAN_URL).catch((error) => {
          console.warn("[ScienceExpoDigitalHuman] 预缓存数字人 ZIP 失败:", error);
        });
      }
    };

    loadConfigAndPreload();
    return () => {
      cancelled = true;
    };
  }, [expertName, workflowName, messageType, configOverride, activeSpeechConfigKey]);

  const keyingRef = useRef(null);
  useEffect(() => {
    if (!isSpotlight || !containerRef.current) return undefined;
    const keying = startBlackBackgroundKeying(containerRef.current);
    keyingRef.current = keying;
    keying.setEnabled(!!activeRef.current);
    return () => {
      keyingRef.current = null;
      keying.stop();
    };
  }, [isSpotlight, config?.avatarPath]);

  useEffect(() => {
    keyingRef.current?.setEnabled(!!active);
  }, [active]);

  /**
   * 中央 spotlight 数字人 - 关键策略：
   *   - 首次（avatarRef 为空）才 new ExpertAvatar + startSession
   *   - 后续切换角色：调用 avatar.switchExpert，不卸载组件、不销毁实例
   *     - 同 zip → 仅重建 session/WS/processor，亚秒级，无视觉中断
   *     - 不同 zip → 走 loadAvatar，用 onLoadingChange 上报"加载中"，由父组件渲染过渡卡
   *
   * ⚠️ 依赖数组只放真正会触发"换角色"的字段（config.id / config.avatarPath / config.voice）。
   *    onLoadingChange / onPlaybackEnd / currentUser 用 ref 旁路，避免父组件每次 render
   *    都触发 useEffect cleanup + re-run（StrictMode 下尤其严重，会导致 startSession
   *    刚完成就被 cancelled 销毁）。
   */
  // 用 ref 包装不稳定的 props/回调
  const onLoadingChangeRef = useRef(onLoadingChange);
  const onPlaybackEndRef = useRef(onPlaybackEnd);
  const onPlaybackStartRef = useRef(onPlaybackStart);
  const currentUserRef = useRef(currentUser);
  const activeRef = useRef(active);
  const statusRef = useRef(status);
  const visualReadyRef = useRef(visualReady);
  useEffect(() => { onLoadingChangeRef.current = onLoadingChange; }, [onLoadingChange]);
  useEffect(() => { onPlaybackEndRef.current = onPlaybackEnd; }, [onPlaybackEnd]);
  useEffect(() => { onPlaybackStartRef.current = onPlaybackStart; }, [onPlaybackStart]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { visualReadyRef.current = visualReady; }, [visualReady]);

  /* P3 多数字人共存：active 切换 → ExpertAvatar.setActive
   * 仅 spotlight；avatar 还没建好时 ensureAvatar 内会再同步一次（用 activeRef）。 */
  useEffect(() => {
    if (!isSpotlight) return;
    const av = avatarRef.current;
    if (!av || !av.setActive) return;
    av.setActive(!!active);
  }, [isSpotlight, active]);

  useEffect(() => {
    if (!isSpotlight || !isOpen || !containerRef.current || !config?.avatarPath) return undefined;
    // P3 lazy mount 闸门：尚未被 stage 请求 mount 且本实例还没建好 → 跳过。
    // 已建好的实例（avatarRef.current 非空）继续走 switchExpert 路径不受影响。
    if (!mountReady && !avatarRef.current) return undefined;

    const token = (tokenRef.current += 1);
    const fullAvatarUrl = `${DIGITAL_HUMAN_URL}${config.avatarPath}`;
    const buildSessionConfig = () => ({
      voice: config.voice,
      model_name: config.model_name,
      session_user: buildSessionUser(currentUserRef.current),
      background: "#020611",
      backgroundColor: "#020611",
    });

    // ⚡ 同步加锁：如果是切换角色（avatarRef 已有实例），立即锁住 drainQueue。
    // 必须在调用 ensureAvatar() 之前同步执行，否则 async 函数体内的赋值
    // 会在下一个 microtask 才生效，drainQueue useEffect 已经跑过了。
    if (avatarRef.current) {
      switchingRef.current = true;
      setStatus("switching");
    }

    // 内部 loading handler：既驱动 visualReady（PNG 显隐），也透传给父组件
    const handleLoadingChange = (loading, reason) => {
      setVisualReady(!loading);
      onLoadingChangeRef.current?.(loading, reason);
    };

    const ensureAvatar = async () => {
      try {
        if (!avatarRef.current) {
          setStatus("loading");
          setVisualReady(false);
          onLoadingChangeRef.current?.(true, "初始化数字人…");
          const avatar = new ExpertAvatar(containerRef.current, DIGITAL_HUMAN_URL);
          avatarRef.current = avatar;
          await avatar.startSession(config.id, fullAvatarUrl, buildSessionConfig());
          if (token !== tokenRef.current) return;
          setStatus("ready");
          setVisualReady(true);
          onLoadingChangeRef.current?.(false);
          // P3 多数字人共存：启动期全员 mount 但只有 active 那个露脸渲染
          // 这里 mount 完成后先按当前 active 状态同步一次（默认非 active → 隐藏 + 停 RAF）
          try { avatar.setActive?.(!!activeRef.current); } catch (_) {}
        } else {
          // 切换角色：switchExpert 现在 session ready 即 resolve（loadAvatar fire-and-forget）
          // → drainQueue 可立即 speak，音频抢先播放
          // → loadAvatar 异步完成时通过 handleLoadingChange(false) 通知 visualReady=true
          setVisualReady(false);
          try {
            await avatarRef.current.switchExpert(
              config.id,
              fullAvatarUrl,
              buildSessionConfig(),
              handleLoadingChange
            );
          } finally {
            switchingRef.current = false;
          }
          if (token !== tokenRef.current) return;
          setStatus("ready");
        }
      } catch (error) {
        console.error("[ScienceExpoDigitalHuman] 中央数字人切换失败:", error);
        switchingRef.current = false;
        setStatus("error");
        setVisualReady(true); // 降级时不要永远盖着 PNG
        onLoadingChangeRef.current?.(false);
        onPlaybackEndRef.current?.();
      }
    };

    ensureAvatar();

    return () => {
      // 仅 invalidate token，不销毁。下一次 effect 用 switchExpert 复用实例。
      tokenRef.current += 1;
    };
  }, [isSpotlight, isOpen, config?.id, config?.avatarPath, config?.voice, config?.model_name, mountReady]);

  /**
   * ⚠️ 关键决策：不在 React unmount 时主动 destroy ExpertAvatar。
   *
   * 原因：
   *   1. React 18 StrictMode 在开发模式下会 mount → cleanup → mount，
   *      立即 destroy 会毁掉刚建好的实例，导致首个角色既无形象也无声音
   *      （日志里 "active processor 注册" 后 1ms 就 destroy）。
   *   2. unmount 时 React 可能复用同一个 DOM container 给 re-mount，
   *      destroy 中的 container.innerHTML = '' 会把新实例刚挂上去的
   *      共享 canvas 一起清掉。
   *   3. ScienceExpoStage 整体卸载（用户关闭房间）时浏览器会 GC 回收
   *      AudioContext/WebSocket，不主动 destroy 不会造成长期泄漏。
   *
   * 真正需要释放资源的场景由 ExpertAvatar 内部的 switchExpert 处理（关旧 WS）。
   */

  // 等待 switchExpert 完成。
  // ⚠️ 阈值必须 ≥ loadAvatar 真实耗时（实测 13–17s），否则文本会被错误地"暂停等待"。
  // 30s 覆盖 P95；超时后调用方负责把文本退回队首，绝不静默丢弃。
  const waitForSwitchDone = async () => {
    if (!switchingRef.current) return true;
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (!switchingRef.current) { resolve(true); return; }
        if (Date.now() - start > 30000) { resolve(false); return; }
        setTimeout(check, 30);
      };
      check();
    });
  };

  const drainQueue = async () => {
    if (queueRunningRef.current || !avatarRef.current) return;
    queueRunningRef.current = true;

    try {
      // ⚠️ 每次取下一段文本前都重新检查切换锁，并重新读 avatarRef.current。
      // 因为：drainQueue 在主持人 await playbackDone 期间仍在运行，
      // 期间 React 把新文本 push 到队列、把 config.id 切换为新角色（switchExpert 已置锁）。
      // 此时若不重检，循环会继续用旧 avatar 跑新文本，导致新角色"无声无形"。
      while (queueRef.current.length > 0) {
        // 1. 等切换完成；超时不退出循环、不丢文本，下一轮再等一次
        //    （切换若真卡死，drainQueue 不会饿死后续文本——播放队列不再静默被吞）
        if (switchingRef.current) {
          const waited = await waitForSwitchDone();
          if (!waited) {
            console.warn("[ScienceExpoDigitalHuman] ⏱️ 切换超时 30s，继续等待，不丢文本");
            continue;
          }
        }
        // 2. 等待首次模型/会话 ready。录课模式下脚本推进很快，
        // 文本可能在新角色刚 mount、status 仍 loading 时进入队列；
        // 此时直接 speak 容易出现 HTTP 成功但无音频/表情回放。
        // status=ready 只代表 session 可用；switchExpert 会异步 loadAvatar，
        // 必须等 visualReady=true，确保模型已注册 processor 后再 speak。
        if ((statusRef.current !== "ready" && statusRef.current !== "switching") || !visualReadyRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 60));
          continue;
        }
        // 3. 重新读最新 avatar 实例
        const currentAvatar = avatarRef.current;
        if (!currentAvatar) break;

        // switchExpert 对不同 zip 会 fire-and-forget 地加载 3D 形象；
        // 对录课脚本这种快速推进场景，必须等该 Promise 完成后再 speak，
        // 否则会出现 HTTP speak 成功但没有声音/口型/动态。
        if (currentAvatar.loadAvatarPromise) {
          try {
            await currentAvatar.loadAvatarPromise;
          } catch (_) {}
          if (switchingRef.current || avatarRef.current !== currentAvatar) {
            continue;
          }
        }

        const next = queueRef.current.shift();
        if (!next) continue;

        setStatus("speaking");
        console.log("[ScienceExpoDigitalHuman] 准备 speak:", {
          expertName: displayName,
          speechKey,
          active: activeRef.current,
          status: statusRef.current,
          visualReady: visualReadyRef.current,
          expertId: currentAvatar.expertId,
          sessionId: currentAvatar.sessionId,
          wsState: getWsStateName(currentAvatar.ws),
          hasProcessor: !!currentAvatar.processor,
          textLength: next.length,
        });
        currentAvatar.setMuted?.(!!silentRef.current);
        const playbackStarted = currentAvatar.processor?.waitForPlaybackStart
          ? currentAvatar.processor.waitForPlaybackStart(15000)
          : Promise.resolve();
        const playbackDone = currentAvatar.speak(next, null);
        await playbackStarted;
        // 等待期间发生了切换：跳出当前播放，下一轮循环从新 avatar 重发
        if (switchingRef.current || avatarRef.current !== currentAvatar) {
          // 把当前文本退回队首，等切完后重新发给新 avatar
          queueRef.current.unshift(next);
          continue;
        }
        // ⚠️ 必须用 ref 调用最新回调：drainQueue 是 render 时建的闭包，
        // 切换角色后老 drainQueue 仍在循环，若用闭包内的 onPlaybackStart 会拿到 R1
        // 那一刻的 centerAgentEntry/activeSpeechText（旧角色），导致字幕停在旧角色文本。
        onPlaybackStartRef.current?.();
        await playbackDone;
      }
      setStatus("ready");
    } catch (error) {
      console.error("[ScienceExpoDigitalHuman] 播放数字人语音失败:", error);
      setStatus("error");
    } finally {
      queueRunningRef.current = false;
      onPlaybackEndRef.current?.();
    }
  };

  useEffect(() => {
    // 只在 ready / switching 时入队（idle=未初始化, loading=首次加载, error=降级）
    if (!isSpotlight || !active || !isOpen) return;
    if (status !== "ready" && status !== "switching") return;
    const cleaned = String(text || "").trim();
    const spokenKey = `${speechKey || "default"}:${cleaned}`;
    if (!cleaned || spokenKey === lastSpokenRef.current) return;

    lastSpokenRef.current = spokenKey;
    queueRef.current.push(cleaned);
    void drainQueue();
  }, [isSpotlight, active, isOpen, status, text, speechKey]);

  return (
    <div className={`science-expo-digital-human ${variant} ${active ? "active" : ""} status-${status}`}>
      {isSpotlight ? (
        <>
          <div className="science-expo-avatar-renderer" ref={containerRef} />
          {/* 3D 未就绪时显示 PNG 占位（与 seat 候场图同款），声音可先行播放
              visualReady=true 时通过 is-hidden 渐隐，不立即 unmount，避免突兀切换 */}
          {showSpotlightPreview && (
            <img
              className="science-expo-avatar-spotlight-preview"
              src={previewUrl}
              alt={displayName}
              draggable={false}
              onError={() => setPreviewFailed(true)}
            />
          )}
        </>
      ) : previewUrl && !previewFailed && config?.avatarPath && !config?._fallback ? (
        <img
          className="science-expo-avatar-preview"
          src={previewUrl}
          alt={displayName}
          draggable={false}
          onError={() => setPreviewFailed(true)}
        />
      ) : (
        <div className="science-expo-avatar-standby">
          <div className="standby-face">{String(displayName).slice(0, 1)}</div>
          <div className="standby-rings" />
        </div>
      )}

      <div className="science-expo-avatar-name">{displayName}</div>
      {status === "loading" && <div className="science-expo-avatar-status">加载中</div>}
      {status === "error" && <div className="science-expo-avatar-status error">已降级</div>}
    </div>
  );
};

export default ScienceExpoDigitalHuman;
